import { DocumentStatus } from '@kanbu/schema/enums';
import throttle from 'lodash.throttle';

import { invalidateKB } from '@/services/invalidateKB';
import { trpcClient } from '@/services/trpc';

import type { FileUploadAdapter } from '../fileUploadTypes';

/**
 * Throttle invalidation to avoid too many requests when file uploads finish.
 */
const throttledInvalidate = throttle(invalidateKB, 3000);

export const createDocumentUploadAdapter = (
  chatIds: string[],
  availableForAllAgents: boolean = false,
  folderId?: string | null,
  onSuccess?: () => void,
): FileUploadAdapter => {
  return {
    upload: async (file: File): Promise<void> => {
      // Get signed URL from backend
      try {
        const response = await trpcClient.documents.createDocument.mutate({
          fileName: file.name,
          contentType: file.type,
          size: file.size,
          chats: chatIds,
          availableForAllAgents,
          folderId: folderId ?? null,
        });

        const uploadResponse = await fetch(response.uploadUrl, {
          method: 'PUT',
          body: file,
          mode: 'cors',
          headers: {
            'Content-Type': file.type,
          },
        });

        if (!uploadResponse.ok) {
          throw new Error('Failed to upload file to storage');
        }

        await trpcClient.documents.update.mutate({
          id: response.document.id,
          status: DocumentStatus.Ready,
        });

        throttledInvalidate();
        onSuccess?.();
      } catch (error) {
        // Check for Document Limit Error
        if (
          typeof error === 'object' &&
          error &&
          'message' in error &&
          typeof error.message === 'string' &&
          error.message.startsWith('Adding this document would exceed')
        ) {
          throw new Error(error.message);
        }

        throw new Error(
          `Upload failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        );
      }
    },
  };
};
